import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";

import { useMountEffect } from "../../../hooks/useMountEffect";
import { IProduct } from "../../../models/catalogue";
import { BreakPoint } from "../../../models/screen";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { GridAccordion } from "../GridAccordion";
import { GridFilterOption } from "../GridFilterOption";
import { defaults } from "../defaults";
import { GridFilterConfig } from "../filters";
import { GridFilterSelectionMode, IFilterOption } from "../models";
import {
    updateFilterVisibility,
    updateSelectManyFilterValue,
    updateSelectOneFilterValue,
} from "../reducers";
import { IFilterState } from "../reducers.interfaces";

import styles from "./index.module.scss";

const buildSelectedValuesLabels = (
    filterOptions: IFilterOption[],
    filterValueState: IFilterState,
): string[] => {
    const labels: string[] = [];
    const selectedValues = new Set(
        filterValueState ? filterValueState.selectedValues : [],
    );
    for (const option of filterOptions) {
        if (selectedValues.has(option.id)) {
            labels.push(option.label);
        }
    }
    return labels;
};

const buildSelectOneFilterBody = (
    config: GridFilterConfig,
    options: IFilterOption[],
    selectedValues: readonly string[],
    products: readonly IProduct[],
    onSelectOneChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string | null,
    ) => void,
): React.ReactNode => {
    const selectID = `grid-filter-${config.filterID}`;
    return (
        <div className={styles.sizeSelector}>
            <select
                id={selectID}
                name={selectID}
                className={styles.sizeSelectorSelect}
                value={selectedValues[0] || ""}
                onChange={(e) => {
                    onSelectOneChange(
                        products[0]?.product_class_slug,
                        config.filterID,
                        e.currentTarget.value,
                    );
                }}
            >
                <option value={""}>Select</option>
                {options.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const buildSelectManyFilterBody = (
    config: GridFilterConfig,
    options: IFilterOption[],
    onSelectManyChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string,
        isSelected: boolean,
    ) => void,
): React.ReactNode => {
    return (
        <>
            {options.map((option) => (
                <GridFilterOption
                    key={option.id}
                    filterID={config.filterID}
                    option={option}
                    onChange={onSelectManyChange}
                />
            ))}
        </>
    );
};

const buildFilterBody = (
    config: GridFilterConfig,
    products: readonly IProduct[],
    selectedValues: readonly string[],
    onSelectOneChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string | null,
    ) => void,
    onSelectManyChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string,
        isSelected: boolean,
    ) => void,
): React.ReactNode => {
    const options = config.listFilterOptions(products);
    if (config.selectionMode === GridFilterSelectionMode.SELECT_ONE) {
        return buildSelectOneFilterBody(
            config,
            options,
            selectedValues,
            products,
            onSelectOneChange,
        );
    }
    return buildSelectManyFilterBody(config, options, onSelectManyChange);
};

export const GridFilter = React.memo(
    (
        props: Immutable<{
            config: GridFilterConfig;
            showSelectedList: boolean;
        }>,
    ): React.ReactNode => {
        const dispatch = useAppDispatch();
        const visibilityID = props.config.filterID;

        const products = useAppSelector(
            (rootState) => (rootState.productgrid2 || defaults).products,
        );
        const filterState = useAppSelector(
            (rootState) =>
                (rootState.productgrid2 || defaults).filters[visibilityID],
        );
        const valueState = useAppSelector(
            (rootState) =>
                (rootState.productgrid2 || defaults).filters[
                    props.config.filterID
                ],
        );
        const options = props.config.listFilterOptions(products);
        const isOpen = filterState ? filterState.isOpen : true;
        const selectedValues = valueState ? valueState.selectedValues : [];
        const selectedValuesLabels = buildSelectedValuesLabels(
            options,
            valueState,
        );

        const onSetVisibility = (id: string, isOpen: boolean) => {
            dispatch(
                updateFilterVisibility({
                    filterID: id,
                    isOpen: isOpen,
                }),
            );
        };

        const onSelectOneChange = (
            optionNamespace: string,
            filterID: string,
            optionID: string | null,
        ) => {
            dispatch(
                updateSelectOneFilterValue({
                    optionNamespace: optionNamespace,
                    filterID: filterID,
                    optionID: optionID,
                }),
            );
        };

        const onSelectManyChange = (
            optionNamespace: string,
            filterID: string,
            optionID: string,
            isSelected: boolean,
        ) => {
            dispatch(
                updateSelectManyFilterValue({
                    optionNamespace: optionNamespace,
                    filterID: filterID,
                    optionID: optionID,
                    isSelected: isSelected,
                }),
            );
        };

        useMountEffect(() => {
            const currentBreakpoint = getViewportBreakpoint();
            if (currentBreakpoint <= BreakPoint.MEDIUM) {
                onSetVisibility(visibilityID, false);
            }
        });

        // An accordion with no options is a control that opens onto nothing,
        // so it is worse than absent: it reads as a filter the shopper can use.
        //
        // Not a hypothetical state. The options of an attribute-derived filter
        // come from `state.productgrid2.products`, and `loadProducts.pending`
        // empties that list -- so every such filter is option-less for the
        // length of a fetch, which is exactly the window `ProductGrid` now
        // keeps its chrome mounted through. Range filters build their options
        // from config and are unaffected, so what stays drawn during a load is
        // what is actually usable during one. The same guard covers a category
        // whose products carry none of the attribute at all.
        //
        // The leaf half of the rule only. Whether the chrome *around* these
        // accordions should exist -- a heading, the sidebar's 200px track, a
        // Filter button that opens a panel -- is a question only the parents
        // can answer, and they narrow with `listNonEmptyFilters` so that both
        // answers come from the same place.
        //
        // After all hooks, not before: an early return above `useMountEffect`
        // would make it conditional.
        if (options.length <= 0) {
            return null;
        }

        return (
            <GridAccordion
                bodyID={`filter-body-${props.config.filterID}`}
                title={
                    <>
                        {props.config.label}
                        {props.showSelectedList && (
                            <span className={styles.selectedFiltersList}>
                                {selectedValuesLabels.join(", ")}
                            </span>
                        )}
                    </>
                }
                isOpen={isOpen}
                onToggle={() => {
                    onSetVisibility(visibilityID, !isOpen);
                }}
                bodyClassName={classNames({
                    "product-grid-filter__body--select-many":
                        props.config.selectionMode ===
                        GridFilterSelectionMode.SELECT_MANY,
                    "product-grid-filter__body--select-one":
                        props.config.selectionMode ===
                        GridFilterSelectionMode.SELECT_ONE,
                })}
            >
                {buildFilterBody(
                    props.config,
                    products,
                    selectedValues,
                    onSelectOneChange,
                    onSelectManyChange,
                )}
            </GridAccordion>
        );
    },
);
